import React, { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './Tour.module.scss';
import cn from 'classnames';
import Button from '@components/Button';
import { CommandBarModal } from '@views/CommandBar';
import {
  AsperandIcon,
  NotesIcon,
  PersonIcon,
  RemindersIcon,
  SearchIcon,
  StarIcon,
  TagIcon,
  ProfileIcon,
} from '@components/Icons/TourIcons';
import { SVGProps } from '@components/Icons/SVGWrapper';
import analytics from '@support/analytics';
import { isWindows } from '@support/platform-checks';
import gradientBG from '@app/img/onboarding/gradient_bg.png';
import { useTheme } from '@support/hooks/use-theme';
import { useSelector } from 'react-redux';
import { selectNavBarWidth } from '@reducers/ui/panelSizing';

interface Pointer {
  Icon: (props: SVGProps) => ReactElement<any, any>;
  content: string;
}

interface TourToolTipSchema {
  title: string;
  content: string;
  pointers?: Pointer[];
  targetSelector?: string;
  placement?: 'right' | 'top' | 'bottom';
  className?: string;
}

const tourSteps: TourToolTipSchema[] = [
  {
    title: 'Search',
    content:
      'Just type what comes to mind and Clay will find the right person or group of people based on your notes and their profile.',
    pointers: [
      {
        Icon: SearchIcon,
        content:
          'Search by location, company name, when you last saw them, what you talked about—anything really',
      },
      {
        Icon: TagIcon,
        content: 'Combine queries together to create advanced searches with tags and filters',
      },
    ],
    targetSelector: '#search-input',
  },
  {
    title: 'Home',
    content:
      'Never miss a beat with Home—a powerful, high-level view of all the updates from your entire network.',
    pointers: [
      {
        Icon: ProfileIcon,
        content: 'Click a person to view their profile',
      },
      {
        Icon: StarIcon,
        content: 'Star your favorite people',
      },
      {
        Icon: AsperandIcon,
        content: 'Important news from key relationships',
      },
    ],
    targetSelector: '#home-navigation',
  },
  {
    title: 'People',
    content: 'Browse all the people you know, collected together in one place.',
    targetSelector: '#people-navigation',
  },
  {
    title: 'Groups',
    content:
      "The most powerful way to organize your relationships. We've given you a head start by grouping some of your closest people as favorites.",
    targetSelector: '#groups-section',
  },
  {
    title: 'Add',
    content:
      "Add anyone to Clay, and we'll magically populate their profiles with all the info you need.",
    pointers: [
      {
        Icon: RemindersIcon,
        content: 'Set follow up reminders in plain English',
      },
      {
        Icon: NotesIcon,
        content: 'Take notes about important details and @ mention related people',
      },
      {
        Icon: PersonIcon,
        content: 'Quickly add new people using their email, Twitter, LinkedIn, or phone #',
      },
    ],
    targetSelector: '#new-person-btn',
    placement: 'top',
    className: styles.addStep,
  },
  {
    title: 'Nexus',
    content: `Opt in to ask our powerful AI questions about people in your network. For example, to prepare for a meeting you might request, "Please summarize Susan's background for me."`,
    targetSelector: '#nexus-btn',
    placement: 'top',
    className: styles.addStep,
  },
  {
    title: 'Clay Command',
    content: `Hit ${
      isWindows() ? 'Ctrl' : 'Command'
    } + K to bring up our command palette. You can instantly do or search for any action from here.`,
    className: styles.commandBarStep,
  },
  {
    title: 'Help & Settings',
    content: `Our friendly mascot Udo serves as your one stop shop for settings, help, and so much more.`,
    className: styles.udoStep,
    placement: 'right',
  },
];

const getTarget = (selector) => {
  let target = null;

  try {
    target = document.querySelector(selector);
    if (!target) {
      throw new Error('Target not found.');
    }
  } catch (e) {
    console.warn(`Tour target not found: "${selector}"`);
  }

  return target;
};

interface TourToolTipProps extends TourToolTipSchema {
  step: number;
  onContinue: () => void;
  index: number;
}

function TourToolTip({
  step,
  title,
  content,
  pointers,
  onContinue,
  index,
  className,
  ...props
}: TourToolTipProps) {
  const [theme] = useTheme();
  const [targetPos, setTargetPos] = useState<DOMRect | null>(null);
  const navBarWidth = useSelector(selectNavBarWidth);
  const targetRef = useRef(null);
  const active = step == index;

  const style = useMemo(() => {
    if (!targetPos) {
      return {};
    }

    if (targetPos && props.placement == 'top') {
      const halfWidth = targetPos.width / 2;
      return {
        top: `${targetPos.top - 7.5}px`,
        left: `${targetPos.x + halfWidth - navBarWidth}px`,
      };
    } else if (targetPos && props.placement === 'bottom') {
      return {
        top: `${targetPos.bottom + 16}px`,
        left: `${targetPos.x - navBarWidth}px`,
      };
    }

    return {
      top: `${targetPos.top}px`,
    };
  }, [targetPos]);

  const handleElResize = ([entry]) => {
    setTargetPos(entry?.target?.getBoundingClientRect() || null);
  };

  const handleWindowResize = () => {
    setTargetPos(targetRef.current?.getBoundingClientRect() || null);
  };

  useEffect(() => {
    targetRef.current = !!props.targetSelector && getTarget(props.targetSelector);
    if (!targetRef.current) {
      return void 0;
    }
    const observer = new ResizeObserver(handleElResize);
    observer.observe(targetRef.current);
    return () => observer.disconnect();
  }, [active]);

  useEffect(() => {
    window.addEventListener('resize', handleWindowResize);
    return () => window.removeEventListener('resize', handleWindowResize);
  }, []);

  return (
    <div
      style={style}
      className={cn(
        styles.tourToolTip,
        active && styles.active,
        props.placement == 'top' && styles.topPlacement,
        props.placement === 'bottom' && styles.bottomPlacement,
        props.placement === 'right' && styles.rightPlacement,
        step > index && styles.passedStep,
        className,
      )}
    >
      <div className={styles.bg}>
        <div className={styles.rainbowBG}></div>
      </div>

      {props.placement === 'right' ? (
        <svg className={cn(styles.rightCaret, styles[theme])} viewBox="0 0 14 14">
          <path d="M-0.5 0.5 h 9 c 3.2 -0.25 3.75 1.5 3 3 L -0.5 14 z" strokeWidth="1" />
        </svg>
      ) : (
        <div className={styles.caret}>
          <div className={styles.rainbowBG}></div>
        </div>
      )}
      <h4 className={styles.title}>{title}</h4>
      <p className={styles.content}>{content}</p>
      {pointers && (
        <div className={styles.pointers}>
          {pointers.map(({ Icon, content }, idx) => (
            <React.Fragment key={idx}>
              <div className={styles.pointerIcon}>
                <Icon />
              </div>
              <div>{content}</div>
            </React.Fragment>
          ))}
        </div>
      )}
      <div className={styles.stepLabel}>
        {index + 1} / {tourSteps.length}
      </div>
      <Button className={styles.btn} onClick={onContinue}>
        Continue
      </Button>
    </div>
  );
}

export default function Tour() {
  const navigate = useNavigate();
  const [theme] = useTheme();
  const [step, setStep] = useState(0);
  const [commandBarModalOpen, setCommandBarModalOpen] = useState(false);

  const skipTour = () => {
    analytics.track('Tutorial Skipped');
    setStep(tourSteps.length);
  };

  useEffect(() => {
    analytics.track('Onboarding in Progress', { screen: 'Tutorial' });
  }, []);

  useEffect(() => {
    if (tourSteps[step]?.title == 'Clay Command') {
      setCommandBarModalOpen(true);
    }

    return () => setCommandBarModalOpen(false);
  }, [step]);

  useEffect(() => {
    const $el = document.querySelector(tourSteps[step]?.targetSelector);
    $el?.classList.add('hover');

    return () => $el?.classList.remove('hover');
  }, [step]);

  useEffect(() => {
    if (step >= tourSteps.length) {
      navigate('/', { replace: true });
    }
  }, [step, navigate]);

  return (
    step < tourSteps.length && (
      <>
        {theme === 'dark-black' && <div className={styles.darkBackgroundAdjust} />}

        <div className={styles.bgImageContainer}>
          <img src={gradientBG} className={styles.bgImage} />
        </div>

        <div className={styles.tourWrapper}>
          {tourSteps.map((tourStep, index) => (
            <TourToolTip
              key={index}
              index={index}
              step={step}
              onContinue={() => setStep(step + 1)}
              {...tourStep}
            />
          ))}
          <Button className={cn(styles.btn, styles.skipBtn)} onClick={skipTour}>
            Skip
          </Button>
        </div>
        <CommandBarModal
          id="command-bar-modal"
          closeModal={() => setCommandBarModalOpen(false)}
          overlayClassName={cn(styles.modalContainer, commandBarModalOpen && styles.commandActive)}
          skipAutoFocus
        />
      </>
    )
  );
}
